import type { ComponentType } from 'react';
import { cn } from '@/utils/cn';

interface ViewMode<T extends string> {
  value: T;
  icon: ComponentType<{ className?: string }>;
  label: string;
}

interface ViewToggleProps<T extends string> {
  modes: ViewMode<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
}

/**
 * Generic view-mode toggle atom — segmented control with icon + text label.
 *
 * Renders a compact row of labeled buttons — one per mode. The active mode
 * button has a filled accent background; inactive buttons are ghost-styled.
 *
 * Designed to toggle between gallery / list views but generic enough to reuse
 * for any pair (or group) of view modes elsewhere in the UI.
 *
 * Accessibility:
 * - Each button has aria-pressed and aria-label.
 * - Min 44px height hit target (touch accessibility).
 * - Keyboard-navigable (buttons are naturally focusable + activatable with
 *   Enter / Space).
 */
// Exported as both named and default to support both import styles.
export function ViewToggle<T extends string>({
  modes,
  value,
  onChange,
  className,
}: ViewToggleProps<T>) {
  return (
    <div
      className={cn('inline-flex items-center rounded-lg border border-border-subtle bg-dark-elevated-1 p-0.5', className)}
      role="group"
    >
      {modes.map((mode) => {
        const Icon = mode.icon;
        const isActive = mode.value === value;

        return (
          <button
            key={mode.value}
            type="button"
            aria-pressed={isActive}
            aria-label={mode.label}
            onClick={() => onChange(mode.value)}
            className={cn(
              'flex min-h-[44px] items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50',
              isActive
                ? 'bg-accent text-dark-base'
                : 'text-text-muted hover:bg-dark-elevated-2 hover:text-text-primary',
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{mode.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Default export for barrel re-exports.
export default ViewToggle;
